const IMPORT_NOTE_PREFIX = "[golf-homes-realtor-import";

/** Strip internal import tokens from vendor notes for public display. */
export function stripVendorImportNotes(notes) {
  if (!notes) {
    return "";
  }
  return notes
    .split("\n")
    .map((line) => line.trim())
    .filter(
      (line) =>
        line &&
        !line.includes(IMPORT_NOTE_PREFIX) &&
        !line.startsWith("Primary contact:")
    )
    .join("\n")
    .trim();
}

export function normalizeWebsiteUrl(url) {
  if (!url) {
    return null;
  }
  const trimmed = String(url).trim();
  if (!trimmed) {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

export function vendorPersonName(vendor = {}) {
  if (vendor.primaryContactName) {
    return vendor.primaryContactName;
  }
  const fromParts = [vendor.primaryContactFirstName, vendor.primaryContactLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fromParts || "";
}

/** Display name for agent cards: person name when available, else vendor/business name. */
export function vendorAgentDisplayName(vendor = {}) {
  const person = vendorPersonName(vendor);
  if (person) {
    return person;
  }
  return vendor.vendorName || "Golf Homes Realtor";
}

/** Company line when vendor name differs from the person's name. */
export function vendorAgentCompany(vendor = {}) {
  const person = vendorPersonName(vendor);
  const business = vendor.vendorName || "";
  if (person && business && person !== business) {
    return business;
  }
  if (!person && business) {
    return business;
  }
  return "";
}

export function mapVendorToAgentCard(vendor) {
  const name = vendorAgentDisplayName(vendor);
  const company = vendorAgentCompany(vendor);
  const cleanedNotes = stripVendorImportNotes(vendor.notes);
  const specialty =
    cleanedNotes ||
    (company
      ? `Las Vegas golf homes specialist with ${company}.`
      : "Las Vegas golf homes specialist.");

  const tags = [];
  if (company) {
    tags.push(company);
  }
  if (vendor.vendorType === "Broker") {
    tags.push("Broker");
  } else {
    tags.push("Realtor");
  }

  return {
    id: vendor.id,
    name,
    company,
    specialty,
    photoUrl: vendor.photoUrl || null,
    badge: vendor.vendorType === "Broker" ? "Broker" : "Golf Homes Realtor",
    website: normalizeWebsiteUrl(vendor.website),
    email: vendor.email || null,
    phone: vendor.phone || null,
    tags,
  };
}

/** Resource List vendor card shape from a CRM realtor vendor. */
export function mapVendorToResourceCard(vendor) {
  const agent = mapVendorToAgentCard(vendor);
  return {
    id: agent.id,
    abbr: agentInitials(agent.name),
    name: agent.name,
    category: agent.badge,
    area: agent.company || "Las Vegas Valley",
    description: agent.specialty,
    tags: agent.tags,
    phone: agent.phone,
    email: agent.email,
    website: agent.website,
    photoUrl: agent.photoUrl,
    badge: "recommended",
  };
}

export function agentInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) {
    return "GH";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}
